// frontend/src/components/admin/CostsTab.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../../utils/api';
import { Copy, Check, ChevronUp, ChevronDown, Pencil, Save } from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────
function formatDollars(cents) {
  if (cents == null || isNaN(cents)) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatShortDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Static color maps (PurgeCSS-safe) ────────────────────────────────
const VENDOR_BAR_COLORS = {
  ai: 'bg-purple-400',
  plaid: 'bg-blue-400',
  twilio: 'bg-green-400',
};

const VENDOR_DOT_COLORS = {
  ai: 'bg-purple-400',
  plaid: 'bg-blue-400',
  twilio: 'bg-green-400',
};

const BUDGET_BAR_COLORS = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
};

const BUDGET_TEXT_COLORS = {
  green: 'text-green-700',
  yellow: 'text-yellow-700',
  red: 'text-red-700',
};

function budgetColor(pct) {
  if (pct >= 80) return 'red';
  if (pct >= 50) return 'yellow';
  return 'green';
}

// ── CopyableEmail (inline, same pattern as AdminConsole) ─────────────
function CopyableEmail({ email }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
    } catch {
      const el = document.createElement('textarea');
      el.value = email;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="group flex items-center gap-1.5 text-left hover:text-[#2d7a5f] transition-colors"
      title="Click to copy"
    >
      <span className="truncate max-w-[200px]">{email}</span>
      {copied ? (
        <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
      ) : (
        <Copy className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      )}
    </button>
  );
}

// ── Main Component ───────────────────────────────────────────────────
export default function CostsTab() {
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [perUser, setPerUser] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('total_cents');
  const [sortDir, setSortDir] = useState('desc');
  const [editingBudgets, setEditingBudgets] = useState(false);
  const [budgetDrafts, setBudgetDrafts] = useState({});
  const [savingBudgets, setSavingBudgets] = useState(false);
  const fetchedRef = useRef(false);

  // ── Data fetch ───────────────────────────────────────────────────
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    let cancelled = false;

    async function fetchAll() {
      try {
        const [summaryRes, dailyRes, perUserRes] = await Promise.all([
          api.get('/api/admin/costs/summary'),
          api.get('/api/admin/costs/daily?days=30'),
          api.get('/api/admin/costs/per-user'),
        ]);
        if (!cancelled) {
          setSummary(summaryRes.data);
          setDaily(dailyRes.data.trend || []);
          setPerUser((perUserRes.data.users || []).map(u => ({
            ...u,
            name: [u.first_name, u.last_name].filter(Boolean).join(' ') || null,
          })));
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load cost data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, []);

  // ── Sort ─────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedUsers = useMemo(() => {
    return [...perUser].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Strings
      if (sortField === 'name' || sortField === 'email') {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
      }

      // Numeric
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [perUser, sortField, sortDir]);

  // ── Budget save ──────────────────────────────────────────────────
  const handleBudgetSave = async () => {
    setSavingBudgets(true);
    try {
      await Promise.all(
        Object.entries(budgetDrafts).map(([vendor, dollars]) =>
          api.put('/api/admin/costs/budgets', {
            vendor,
            monthly_budget_cents: Math.round(parseFloat(dollars) * 100),
          })
        )
      );
      // Re-fetch summary for updated budgets
      const res = await api.get('/api/admin/costs/summary');
      setSummary(res.data);
      setEditingBudgets(false);
      setBudgetDrafts({});
    } catch {
      // Silently fail — user can retry
    } finally {
      setSavingBudgets(false);
    }
  };

  // ── Sort header ──────────────────────────────────────────────────
  const SortHeader = ({ field, label, className = '' }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 ${className}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && (
          sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </span>
    </th>
  );

  // ── Loading / Error ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-xl" />
          ))}
        </div>
        <div className="h-64 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">
        {error}
      </div>
    );
  }

  // ── Derived values ───────────────────────────────────────────────
  const budgets = summary?.budgets || [];
  const aiVision = summary?.ai?.vision_cents || 0;
  const aiText = summary?.ai?.text_cents || 0;
  const plaidCalls = summary?.plaid?.calls || 0;
  const twilioMessages = summary?.twilio?.messages || 0;

  // Budget status — worst vendor
  const worstBudget = budgets.reduce((worst, b) => {
    if (!worst || b.pct > worst.pct) return b;
    return worst;
  }, null);
  const worstColor = worstBudget ? budgetColor(worstBudget.pct) : 'green';

  const BUDGET_STATUS_CARD_STYLES = {
    green: 'bg-green-50 border-green-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    red: 'bg-red-50 border-red-200',
  };

  const BUDGET_STATUS_LABEL = {
    green: 'On Track',
    yellow: 'Watch',
    red: 'Over Budget',
  };

  // Daily trend max for bar scaling
  const maxDayCost = daily.reduce((max, d) => {
    const total = (d.ai_cents || 0) + (d.plaid_cents || 0) + (d.twilio_cents || 0);
    return Math.max(max, total);
  }, 0);

  return (
    <>
      {/* ─── Summary Cards ─── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {/* Grand Total */}
        <div className="rounded-xl border p-4 bg-gray-50 border-gray-200">
          <div className="text-2xl font-bold text-[#2c2825]">{formatDollars(summary?.grand_total_cents)}</div>
          <div className="text-xs text-gray-500 font-medium mt-1">Total MTD</div>
        </div>

        {/* AI */}
        <div className="rounded-xl border p-4 bg-purple-50 border-purple-200">
          <div className="text-2xl font-bold text-[#2c2825]">{formatDollars(summary?.ai?.total_cents)}</div>
          <div className="text-xs text-gray-500 font-medium mt-1">AI MTD</div>
          <div className="text-xs text-gray-400 mt-0.5">
            Vision: {formatDollars(aiVision)} / Text: {formatDollars(aiText)}
          </div>
        </div>

        {/* Plaid */}
        <div className="rounded-xl border p-4 bg-blue-50 border-blue-200">
          <div className="text-2xl font-bold text-[#2c2825]">{formatDollars(summary?.plaid?.total_cents)}</div>
          <div className="text-xs text-gray-500 font-medium mt-1">Plaid MTD</div>
          <div className="text-xs text-gray-400 mt-0.5">
            {plaidCalls.toLocaleString()} call{plaidCalls !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Twilio */}
        <div className="rounded-xl border p-4 bg-green-50 border-green-200">
          <div className="text-2xl font-bold text-[#2c2825]">{formatDollars(summary?.twilio?.total_cents)}</div>
          <div className="text-xs text-gray-500 font-medium mt-1">Twilio MTD</div>
          <div className="text-xs text-gray-400 mt-0.5">
            {twilioMessages.toLocaleString()} message{twilioMessages !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Budget Status */}
        <div className={`rounded-xl border p-4 ${BUDGET_STATUS_CARD_STYLES[worstColor]}`}>
          <div className={`text-2xl font-bold ${BUDGET_TEXT_COLORS[worstColor]}`}>
            {BUDGET_STATUS_LABEL[worstColor]}
          </div>
          <div className="text-xs text-gray-500 font-medium mt-1">Budget Status</div>
          {worstBudget && worstColor !== 'green' && (
            <div className="text-xs text-gray-400 mt-0.5">
              {worstBudget.vendor}: {worstBudget.pct}%
            </div>
          )}
        </div>
      </div>

      {/* ─── 30-Day Cost Trend ─── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <h3 className="text-sm font-display font-semibold text-[#2c2825] mb-3">30-Day Cost Trend</h3>

        {daily.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No cost data yet</p>
        ) : (
          <div className="max-h-[400px] overflow-y-auto space-y-1">
            {daily.map((d) => {
              const ai = d.ai_cents || 0;
              const plaid = d.plaid_cents || 0;
              const twilio = d.twilio_cents || 0;
              const total = ai + plaid + twilio;
              const pct = maxDayCost > 0 ? (total / maxDayCost) * 100 : 0;
              const aiPct = total > 0 ? (ai / total) * 100 : 0;
              const plaidPct = total > 0 ? (plaid / total) * 100 : 0;
              const twilioPct = total > 0 ? (twilio / total) * 100 : 0;

              return (
                <div key={d.date} className="flex items-center gap-2 text-xs">
                  <span className="w-12 text-gray-500 flex-shrink-0">{formatShortDate(d.date)}</span>
                  <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                    <div className="h-full flex" style={{ width: `${Math.max(pct, 0.5)}%` }}>
                      {ai > 0 && (
                        <div
                          className={`h-full ${VENDOR_BAR_COLORS.ai}`}
                          style={{ width: `${aiPct}%` }}
                          title={`AI: ${formatDollars(ai)}`}
                        />
                      )}
                      {plaid > 0 && (
                        <div
                          className={`h-full ${VENDOR_BAR_COLORS.plaid}`}
                          style={{ width: `${plaidPct}%` }}
                          title={`Plaid: ${formatDollars(plaid)}`}
                        />
                      )}
                      {twilio > 0 && (
                        <div
                          className={`h-full ${VENDOR_BAR_COLORS.twilio}`}
                          style={{ width: `${twilioPct}%` }}
                          title={`Twilio: ${formatDollars(twilio)}`}
                        />
                      )}
                    </div>
                  </div>
                  <span className="w-14 text-right text-gray-500 flex-shrink-0">{formatDollars(total)}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
          {[
            { label: 'AI', colorKey: 'ai' },
            { label: 'Plaid', colorKey: 'plaid' },
            { label: 'Twilio', colorKey: 'twilio' },
          ].map(({ label, colorKey }) => (
            <div key={label} className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className={`w-2.5 h-2.5 rounded-sm ${VENDOR_DOT_COLORS[colorKey]}`} />
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* ─── Per-User Breakdown ─── */}
      <div className="mb-6">
        <h3 className="text-sm font-display font-semibold text-[#2c2825] mb-3">Per-User Breakdown</h3>

        {sortedUsers.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <p className="text-gray-500 text-sm">No per-user cost data yet</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <SortHeader field="name" label="Name" />
                      <SortHeader field="email" label="Email" />
                      <SortHeader field="ai_cents" label="AI" />
                      <SortHeader field="plaid_cents" label="Plaid" />
                      <SortHeader field="twilio_cents" label="Twilio" />
                      <SortHeader field="total_cents" label="Total" />
                      <SortHeader field="monthly_avg_cents" label="Avg/Mo" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedUsers.map((u) => (
                      <tr key={u.user_id ?? 'system'} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3 text-sm text-[#2c2825] whitespace-nowrap font-semibold">
                          {u.name || '--'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <CopyableEmail email={u.email} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {formatDollars(u.ai_cents)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {formatDollars(u.plaid_cents)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {formatDollars(u.twilio_cents)}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#2c2825] whitespace-nowrap font-semibold">
                          {formatDollars(u.total_cents)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {formatDollars(u.monthly_avg_cents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden space-y-3">
              {sortedUsers.map((u) => (
                <div key={u.user_id ?? 'system'} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-[#2c2825] font-semibold">{u.name || '--'}</div>
                      <CopyableEmail email={u.email} />
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-[#2c2825]">{formatDollars(u.total_cents)}</div>
                      <div className="text-xs text-gray-400">total</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-gray-400 text-xs">AI</span>
                      <div className="text-gray-600">{formatDollars(u.ai_cents)}</div>
                    </div>
                    <div>
                      <span className="text-gray-400 text-xs">Plaid</span>
                      <div className="text-gray-600">{formatDollars(u.plaid_cents)}</div>
                    </div>
                    <div>
                      <span className="text-gray-400 text-xs">Twilio</span>
                      <div className="text-gray-600">{formatDollars(u.twilio_cents)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ─── Budget Thresholds ─── */}
      {budgets.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-display font-semibold text-[#2c2825]">Budget Thresholds</h3>
            {editingBudgets ? (
              <button
                onClick={handleBudgetSave}
                disabled={savingBudgets}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[#2d7a5f] hover:text-[#256b52] transition-colors disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" />
                {savingBudgets ? 'Saving...' : 'Save'}
              </button>
            ) : (
              <button
                onClick={() => {
                  const drafts = {};
                  budgets.forEach(b => { drafts[b.vendor] = (b.monthly_budget_cents / 100).toFixed(2); });
                  setBudgetDrafts(drafts);
                  setEditingBudgets(true);
                }}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </button>
            )}
          </div>

          <div className="space-y-3">
            {budgets.map((b) => {
              const color = budgetColor(b.pct);
              return (
                <div key={b.vendor}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600 font-medium capitalize">{b.vendor}</span>
                    <div className="flex items-center gap-2">
                      {editingBudgets ? (
                        <div className="flex items-center gap-1">
                          <span className="text-gray-400">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="w-20 text-xs border border-gray-300 rounded px-1.5 py-0.5 text-right text-base sm:text-xs"
                            value={budgetDrafts[b.vendor] ?? ''}
                            onChange={(e) => setBudgetDrafts(prev => ({ ...prev, [b.vendor]: e.target.value }))}
                          />
                        </div>
                      ) : (
                        <span className={`font-medium ${BUDGET_TEXT_COLORS[color]}`}>
                          {formatDollars(b.used_cents)} / {formatDollars(b.monthly_budget_cents)}
                        </span>
                      )}
                      <span className={`font-medium ${BUDGET_TEXT_COLORS[color]}`}>
                        {b.pct}%
                      </span>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${BUDGET_BAR_COLORS[color]}`}
                      style={{ width: `${Math.min(b.pct, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
